/**
 * The one place that ever calls `ops.search` — both the typeahead and the
 * History page use this hook rather than fetching independently, so the
 * debounce contract lives in exactly one spot.
 *
 * Debounced, not throttled: a burst of keystrokes clears and restarts the
 * timer each time (the effect's cleanup clears the previous `setTimeout`
 * before starting a new one), so exactly one request fires once the query
 * settles — never one request per keystroke. Search hits the database's
 * full-text index (task-19 resolution #3), so a request per keystroke on a
 * dispatcher's continuous typing is a real load problem, not a theoretical
 * one. `debounceMs` is a parameter (not a module-private constant) so tests
 * can inject 0 for deterministic, near-immediate settling, or a real value
 * to prove the debounce itself with fake timers.
 */
import { useEffect, useState } from 'react'
import { ops } from '../../lib/ops'
import type { SearchResult, SearchScope } from '../../lib/ops'

export const DEFAULT_SEARCH_DEBOUNCE_MS = 300

export interface UseSearchState {
  result: SearchResult | null
  loading: boolean
}

/** An empty (whitespace-only) query never fetches — there is nothing honest to show, so `result` is null rather than "0 results". */
export function useSearch(q: string, scope: SearchScope, debounceMs: number = DEFAULT_SEARCH_DEBOUNCE_MS): UseSearchState {
  const [result, setResult] = useState<SearchResult | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const query = q.trim()
    if (query === '') {
      setResult(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    const timer = setTimeout(() => {
      ops
        .search(query, scope)
        .then((r) => {
          if (!cancelled) {
            setResult(r)
            setLoading(false)
          }
        })
        .catch(() => {
          // A failed search is not a layer refusal worth its own copy here —
          // both callers already have an honest "no results" rendering.
          if (!cancelled) {
            setResult(null)
            setLoading(false)
          }
        })
    }, debounceMs)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [q, scope, debounceMs])

  return { result, loading }
}
